import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { FormFieldMessage } from './FormFieldMessage';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { 
  Upload, 
  File, 
  X, 
  Loader2,
  FileText
} from 'lucide-react';

interface KBCreationInlineFormProps {
  userId: string;
  channelId: string | null;
  channelName: string;
  domainId: string | null;
  domainName: string;
  onComplete: (kbId: string) => void;
  onCancel: () => void;
  disabled?: boolean;
}

export const KBCreationInlineForm = ({
  userId,
  channelId,
  channelName,
  domainId,
  domainName,
  onComplete,
  onCancel,
  disabled = false
}: KBCreationInlineFormProps) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    setIsSubmitting(true);

    try {
      let fileUrl: string | null = null;
      let fileName: string | null = null;
      let fileType: string | null = null;
      let fileSize: number | null = null;

      // Upload file if provided
      if (selectedFile) {
        const uploadFileName = `${userId}/${Date.now()}-${selectedFile.name}`;
        const { error: uploadError } = await supabase.storage
          .from('knowledge-bases')
          .upload(uploadFileName, selectedFile);

        if (uploadError) {
          console.error('File upload error:', uploadError);
          toast.error('Failed to upload file');
          setIsSubmitting(false);
          return;
        }

        const { data: { publicUrl } } = supabase.storage
          .from('knowledge-bases')
          .getPublicUrl(uploadFileName);

        fileUrl = publicUrl;
        fileName = selectedFile.name;
        fileType = selectedFile.type;
        fileSize = selectedFile.size;
      }

      // Create KB record
      const { data: kb, error: kbError } = await supabase
        .from('knowledge_bases')
        .insert({
          creator_id: userId,
          title: title.trim(),
          description: description.trim() || null,
          status: 'draft',
          file_url: fileUrl,
          file_name: fileName,
          file_type: fileType,
          file_size: fileSize,
          channel_id: channelId,
          channel_name: channelName || null,
          domain_id: domainId,
        })
        .select()
        .single();

      if (kbError) {
        console.error('KB creation error:', kbError);
        toast.error('Failed to create Knowledge Base');
        setIsSubmitting(false);
        return;
      }

      // Auto-populate domain if provided
      if (domainId) {
        await supabase.rpc('auto_populate_kb_domains', {
          kb_id: kb.id,
          domain_ids: [domainId]
        });
      }

      // Create attribution
      await supabase.from('attributions').insert({
        knowledge_base_id: kb.id,
        user_id: userId,
        ownership_percentage: 100,
        contribution_type: 'creator'
      });

      toast.success('Knowledge Base created!');
      onComplete(kb.id);
    } catch (error) {
      console.error('Error creating KB:', error);
      toast.error('Failed to create Knowledge Base');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-3 animate-in fade-in slide-in-from-bottom-4 duration-500 w-full max-w-md">
      <FormFieldMessage variant="assistant">
        <div className="flex items-center gap-2 mb-3 text-sm text-muted-foreground">
          <FileText className="h-4 w-4" />
          <span>Creating KB in {channelName || 'Personal'}</span>
          {domainName && <span>• {domainName}</span>}
        </div>

        <div className="space-y-4">
          {/* Title */}
          <div>
            <Label className="text-sm font-medium mb-1.5 block">Title *</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter a descriptive title"
              disabled={disabled || isSubmitting}
            />
          </div>

          {/* Description */}
          <div>
            <Label className="text-sm font-medium mb-1.5 block">Description</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what this knowledge base contains..."
              rows={3}
              disabled={disabled || isSubmitting}
            />
          </div>

          {/* File Upload (Optional) */}
          <div>
            <Label className="text-sm font-medium mb-1.5 block">File (optional)</Label>
            {selectedFile ? (
              <div className="flex items-center justify-between p-2 bg-muted/50 rounded border">
                <div className="flex items-center">
                  <File className="h-4 w-4 mr-2 text-muted-foreground" />
                  <span className="text-sm truncate max-w-[200px]">{selectedFile.name}</span>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-6 w-6 p-0" 
                  onClick={removeFile}
                  disabled={disabled || isSubmitting}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div 
                className="border-2 border-dashed border-border rounded-lg p-4 text-center cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => !disabled && !isSubmitting && fileInputRef.current?.click()}
              >
                <Upload className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  Click to upload a file
                </p>
              </div>
            )}
            <Input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={handleFileSelection}
              disabled={disabled || isSubmitting}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              onClick={onCancel}
              disabled={isSubmitting}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!title.trim() || isSubmitting || disabled}
              className="flex-1"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create KB'
              )}
            </Button>
          </div>
        </div>
      </FormFieldMessage>
    </div>
  );
};
